/**
 * Signals Service
 *
 * Type-safe signals management using @monorepo/shared schemas
 * Signals are standalone measurement points that can be linked to agent actions
 */

import {
  type CreateSignal,
  CreateSignalSchema,
  type PaginatedSignalsResponse,
  PaginatedSignalsResponseSchema,
  type SignalQuery,
  type SignalResponse,
  SignalResponseSchema,
  type UpdateSignal,
  UpdateSignalSchema,
} from '@monorepo/shared/schemas/signals'
import { del, get, post, put } from '@/lib/api-client'

export const signalService = {
  /**
   * List all signals (filtered by query)
   */
  async getSignals(query?: SignalQuery): Promise<PaginatedSignalsResponse> {
    const queryParams = new URLSearchParams()

    if (query?.signal_type) queryParams.append('signal_type', query.signal_type)
    if (query?.aggregation_type)
      queryParams.append('aggregation_type', query.aggregation_type)
    if (query?.status) queryParams.append('status', query.status)
    if (query?.search) queryParams.append('search', query.search)
    if (query?.page) queryParams.append('page', query.page.toString())
    if (query?.limit) queryParams.append('limit', query.limit.toString())

    const url = `/signals${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    return get(url, PaginatedSignalsResponseSchema)
  },

  /**
   * Get signal by ID
   */
  async getSignal(id: string): Promise<SignalResponse> {
    return get(`/signals/${id}`, SignalResponseSchema)
  },

  /**
   * Create new signal
   */
  async createSignal(data: CreateSignal): Promise<SignalResponse> {
    const validated = CreateSignalSchema.parse(data)

    return post('/signals', validated, SignalResponseSchema)
  },

  /**
   * Update signal
   */
  async updateSignal(id: string, data: UpdateSignal): Promise<SignalResponse> {
    const validated = UpdateSignalSchema.parse(data)

    return put(`/signals/${id}`, validated, SignalResponseSchema)
  },

  /**
   * Delete signal
   */
  async deleteSignal(id: string): Promise<void> {
    await del(`/signals/${id}`)
  },
}
